import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { useIntl } from 'react-intl';
import useOnClickOutside from 'use-onclickoutside';
import { PILLOW_SIZE_NAME } from '../../../utils/common/constants';
import { PDP_PAGE_TYPE } from '../../PromoBannerTracker/constants';
import { sortSizeOptions } from '../../../utils/sizes';
import styles from './SizeSelection.module.scss';
import messages from './messages';
import useDrupalSettings from '../../../hooks/useDrupalSettings';

type Size = {
  key: string;
  label: string;
  priceAsText?: string;
  discountedPriceAsText?: string;
  isOutOfStock?: boolean;
  price?: { centAmount: number };
  discountedPrice?: { centAmount: number };
};

type SizeSelectionProps = {
  sizes: Size[];
  selectedKey?: string;
  onSelect: (sizeKey: string) => void;
  hidePrices?: boolean;
  attributeName?: string;
};

const SizeSelection = ({
  sizes,
  selectedKey,
  onSelect,
  hidePrices,
  attributeName,
}: SizeSelectionProps) => {
  const ref = useRef(null);
  const { formatMessage } = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  // Explicitly type the return value of useDrupalSettings for safe destructuring
  const { pdp } = useDrupalSettings() as {
    pdp?: {
      type?: string;
    };
  };
  const pageType = pdp?.type;

  // Smallest to largest. Out of stock sizes keep their place in the list and are
  // rendered disabled, so the order always reads the same way.
  const sizesSortedByPriority = sortSizeOptions(sizes, (size) => size.key);

  const selectedSize = sizes.find((size) => size.key === selectedKey);

  interface HandleSizeSelect {
    (sizeKey: string): void;
  }

  const handleSizeSelect: HandleSizeSelect = (sizeKey) => {
    onSelect(sizeKey);
    setIsExpanded(false);
  };

  interface Price {
    centAmount: number;
  }

  interface SizeWithPrice extends Size {
    price?: Price;
    discountedPrice?: Price;
  }

  const hasDiscountedPrice = (size: SizeWithPrice): boolean =>
    !!(
      size?.discountedPrice &&
      size?.price &&
      size.discountedPrice.centAmount < size.price.centAmount
    );

  useOnClickOutside(ref as unknown as React.RefObject<HTMLDivElement>, () => {
    setIsExpanded(false);
  });

  return (
    <div
      ref={ref}
      className={classNames(styles.container, {
        [styles.opened]: isExpanded,
        [styles['slide-down']]:
          attributeName === PILLOW_SIZE_NAME &&
          isExpanded &&
          pageType === PDP_PAGE_TYPE,
      })}
      data-testid={`${attributeName}-selector`}
    >
      <button
        data-testid={`selected-${selectedSize?.label}`}
        className={styles.selected}
        type='button'
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <div className={styles['selected-label']}>
          <span>{selectedSize?.label}</span>
          {!isExpanded && !hidePrices && (
            <>
              <span>-</span>
              {selectedSize && hasDiscountedPrice(selectedSize) && (
                <span className={styles['selected-price']}>
                  {selectedSize?.priceAsText}
                </span>
              )}
              <span className={styles['selected-discounted']}>
                {selectedSize?.discountedPriceAsText}
              </span>
            </>
          )}
        </div>
        <div className={styles.dropdownImg} />
      </button>

      <div className={styles.list}>
        {sizesSortedByPriority.map((size) => (
          <button
            key={size.key}
            data-testid={size?.label}
            type='button'
            className={classNames(styles['list-item'], {
              [styles['list-item-disabled']]: size.isOutOfStock,
              // The selected size stays bold while another one is hovered. Bold
              // only moves once the hovered size is actually selected.
              [styles['list-item-selected']]: size.key === selectedSize?.key,
            })}
            onClick={() => !size.isOutOfStock && handleSizeSelect(size.key)}
          >
            <span className={styles['list-item-label']}>{size.label}</span>
            {!size.isOutOfStock && !hidePrices && hasDiscountedPrice(size) && (
              <span className={styles['list-item-price']}>
                {size.priceAsText}
              </span>
            )}
            {!size.isOutOfStock && !hidePrices && (
              <span className={styles['list-item-discounted']}>
                {size.discountedPriceAsText}
              </span>
            )}
            {size.isOutOfStock && (
              <span className={styles['list-item-out-of-stock']}>
                {formatMessage(messages.outOfStock)}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SizeSelection;
